
import { Message, Conversation, MessageType, MessageStatus } from '@/types/chat';

// Mock data
const mockData = {
  conversations: [
    {
      id: '1',
      participantId: '101',
      participantName: 'Dr. Sarah Johnson',
      participantAvatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      lastMessage: "I will review your medical records and get back to you.",
      lastMessageType: 'text' as MessageType,
      lastMessageTime: new Date(),
      unreadCount: 2,
      condition: 'Dental Implants'
    },
    {
      id: '2',
      participantId: '102',
      participantName: 'Dr. Michael Chen',
      participantAvatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      lastMessage: "Your appointment is confirmed for tomorrow at 2 PM.",
      lastMessageType: 'text' as MessageType,
      lastMessageTime: new Date(Date.now() - 3600000), // 1 hour ago
      unreadCount: 0,
      condition: 'Teeth Whitening'
    }
  ] as Conversation[],
  
  messages: {
    '1': [
      {
        id: '101',
        senderId: 'doctor',
        text: "Hello, I've reviewed your initial case submission.",
        timestamp: new Date(Date.now() - 86400000), // 1 day ago
        status: 'read' as MessageStatus,
        type: 'text' as MessageType
      },
      {
        id: '102',
        senderId: 'patient',
        text: "Thank you doctor. I'm concerned about the discomfort I'm experiencing.",
        timestamp: new Date(Date.now() - 82800000), // 23 hours ago
        status: 'read' as MessageStatus,
        type: 'text' as MessageType
      },
      {
        id: '103',
        senderId: 'doctor',
        text: "I'll review your medical records and get back to you.",
        timestamp: new Date(Date.now() - 3600000), // 1 hour ago
        status: 'delivered' as MessageStatus,
        type: 'text' as MessageType
      }
    ] as Message[],
    '2': [
      {
        id: '201',
        senderId: 'doctor',
        text: "I've reviewed your teeth whitening case.",
        timestamp: new Date(Date.now() - 172800000), // 2 days ago
        status: 'read' as MessageStatus,
        type: 'text' as MessageType
      },
      {
        id: '202',
        senderId: 'patient',
        text: "When can I schedule an appointment?",
        timestamp: new Date(Date.now() - 86400000), // 1 day ago
        status: 'read' as MessageStatus,
        type: 'text' as MessageType
      },
      {
        id: '203',
        senderId: 'doctor',
        text: "Your appointment is confirmed for tomorrow at 2 PM.",
        timestamp: new Date(Date.now() - 3600000 * 2), // 2 hours ago
        status: 'read' as MessageStatus,
        type: 'text' as MessageType
      }
    ] as Message[]
  }
};

// Dummy notification service functions if they don't exist
const addChatMessageNotification = (receiver: string | number, sender: string, message: string) => {
  console.log(`Notification for ${receiver} from ${sender}: ${message}`);
};

const showMessageNotification = (message: string) => {
  console.log(`Message notification: ${message}`);
};

class ChatService {
  private conversations: Conversation[] = [...mockData.conversations];
  private messages: Record<string | number, Message[]> = { ...mockData.messages };
  
  getAllConversations(): Conversation[] {
    return this.conversations;
  }
  
  getMessages(conversationId: string | number): Message[] {
    const key = String(conversationId);
    return this.messages[key] || [];
  }
  
  markConversationAsRead(conversationId: string | number): { conversations: Conversation[], messages: Message[] } {
    const key = String(conversationId);
    
    // Mark conversation as read
    this.conversations = this.conversations.map(conv => 
      conv.id === key ? { ...conv, unreadCount: 0 } : conv
    );
    
    // Mark all messages as read
    if (this.messages[key]) {
      this.messages[key] = this.messages[key].map(msg => 
        msg.senderId === 'doctor' && msg.status !== 'read' 
          ? { ...msg, status: 'read' as MessageStatus } 
          : msg
      );
    }
    
    return {
      conversations: this.conversations,
      messages: this.messages[key] || []
    };
  }
  
  sendMessage(
    conversationId: string | number,
    text: string,
    type: MessageType = 'text',
    file?: File
  ): { conversations: Conversation[], messages: Message[] } | null {
    const key = String(conversationId);
    const conversation = this.conversations.find(c => c.id === key);
    
    if (!conversation) return null;
    
    // Create a new message
    const newMessage: Message = {
      id: Date.now().toString(),
      senderId: 'patient',
      text,
      timestamp: new Date(),
      status: 'sent' as MessageStatus,
      type: type
    };
    
    // Add media properties if applicable
    if (file) {
      newMessage.mediaName = file.name;
      newMessage.mediaSize = `${(file.size / (1024 * 1024)).toFixed(2)} MB`;
      
      if (type === 'image') {
        // Mock URL for the image
        newMessage.mediaUrl = URL.createObjectURL(file);
        newMessage.mediaType = 'image';
      } else if (type === 'document') {
        newMessage.mediaType = 'document';
        // Mock URL for the document
        newMessage.mediaUrl = '#document-url';
      }
    }
    
    // Add the message to the conversation
    if (!this.messages[key]) {
      this.messages[key] = [];
    }
    
    this.messages[key] = [...this.messages[key], newMessage];
    
    // Update the conversation with the last message
    this.conversations = this.conversations.map(c => 
      c.id === key 
        ? { 
            ...c, 
            lastMessage: text,
            lastMessageType: type,
            lastMessageTime: new Date() 
          } 
        : c
    );
    
    // Send a notification to the other participant
    const receiver = conversation.participantId;
    
    // This would be the user's ID or name in a real app
    const sender = 'You';
    
    // Use the notification service to add a notification
    addChatMessageNotification(receiver, sender, text);
    
    return {
      conversations: this.conversations,
      messages: this.messages[key]
    };
  }
  
  createConversation(participant: {
    id: string | number;
    name: string;
    avatar?: string;
    condition?: string;
  }): string | number {
    const participantIdStr = String(participant.id);
    
    // Check if conversation already exists
    const existingConversation = this.conversations.find(
      c => c.participantId === participantIdStr
    );
    
    if (existingConversation) {
      return existingConversation.id;
    }
    
    // Create a new conversation
    const newConversationId = Date.now().toString();
    
    const newConversation: Conversation = {
      id: newConversationId,
      participantId: participantIdStr,
      participantName: participant.name,
      participantAvatar: participant.avatar || '',
      lastMessage: 'Start a conversation',
      lastMessageType: 'text',
      lastMessageTime: new Date(),
      unreadCount: 0,
      condition: participant.condition || ''
    };
    
    this.conversations.push(newConversation);
    this.messages[newConversationId] = [];
    
    return newConversationId;
  }
  
  getTotalUnreadCount(): number {
    return this.conversations.reduce((acc, conv) => acc + conv.unreadCount, 0);
  }
}

export const chatService = new ChatService();
